/**
 * Base helpers for the Fiscalpop API proxy.
 *
 * All requests in this module go through our OWN back-end, which injects the
 * Fiscalpop proxy secret as a query parameter before forwarding the call to
 * https://api.fiscalpop.com. The secret must never live in the front-end bundle.
 *
 * The back-end mounts the proxy at PROXY_BASE = '/proxy/api/v1'.
 *
 * Base URL resolution order:
 *   1. REACT_APP_FISCALPOP_PROXY_URL (explicit override, no trailing slash)
 *   2. Origin of REACT_APP_HTTP_URL + '/proxy/api/v1'
 *   3. '/proxy/api/v1' (same-origin fallback)
 */

const DEFAULT_PATH_PREFIX = '/proxy/api/v1';

const resolveBaseUrl = (): string => {
    const override = process.env.REACT_APP_FISCALPOP_PROXY_URL;
    if (override) {
        return override.replace(/\/$/, '');
    }
    const httpUrl = process.env.REACT_APP_HTTP_URL;
    if (httpUrl) {
        try {
            return `${new URL(httpUrl).origin}${DEFAULT_PATH_PREFIX}`;
        } catch {
            // fall through
        }
    }
    return DEFAULT_PATH_PREFIX;
};

export const FISCALPOP_PROXY_BASE_URL = resolveBaseUrl();

export const buildProxyUrl = (path: string, query?: Record<string, string | number | boolean | undefined | null>): string => {
    const normalizedPath = path.startsWith('/') ? path : `/${path}`;
    let url = `${FISCALPOP_PROXY_BASE_URL}${normalizedPath}`;
    if (query) {
        const params = Object.entries(query)
            .filter(([, v]) => v !== undefined && v !== null && v !== '')
            .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`);
        if (params.length) {
            url += (url.includes('?') ? '&' : '?') + params.join('&');
        }
    }
    return url;
};

const parseResponse = async <T>(response: Response): Promise<T> => {
    if (response.ok) {
        const contentType = response.headers.get('content-type') || '';
        if (contentType.includes('application/json')) {
            return response.json() as Promise<T>;
        }
        return response.text() as unknown as Promise<T>;
    }
    const errText = await response.text();
    throw new Error(errText);
};

export interface FiscalpopRequestOptions {
    signal?: AbortSignal;
    headers?: Record<string, string>;
}

export const getJson = <T = any>(path: string, query?: Record<string, any>, options: FiscalpopRequestOptions = {}): Promise<T> => {
    return fetch(buildProxyUrl(path, query), {
        method: 'GET',
        signal: options.signal,
        headers: options.headers,
    }).then((r) => parseResponse<T>(r));
};

export const postJson = <T = any>(path: string, body: any = {}, options: FiscalpopRequestOptions = {}): Promise<T> => {
    return fetch(buildProxyUrl(path), {
        method: 'POST',
        body: JSON.stringify(body),
        signal: options.signal,
        headers: {
            'Content-Type': 'application/json',
            ...(options.headers || {}),
        },
    }).then((r) => parseResponse<T>(r));
};

export const postForm = <T = any>(path: string, formData: FormData, options: FiscalpopRequestOptions = {}): Promise<T> => {
    return fetch(buildProxyUrl(path), {
        method: 'POST',
        credentials: 'omit',
        body: formData,
        signal: options.signal,
        headers: options.headers,
    }).then((r) => parseResponse<T>(r));
};

export const postRaw = <T = any>(path: string, body: BodyInit, query?: Record<string, any>, options: FiscalpopRequestOptions = {}): Promise<T> => {
    return fetch(buildProxyUrl(path, query), {
        method: 'POST',
        credentials: 'omit',
        body,
        signal: options.signal,
        headers: options.headers,
    }).then((r) => parseResponse<T>(r));
};
